import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug, parseVariantSlug } from '../gameTypes.js';
import { ON_AND_ON_VARIANTS, ON_AND_ON_EXPECTED_REPLAYS } from './01-on-and-on.js';
import { SKY_HIGH_VARIANTS, SKY_HIGH_EXPECTED_REPLAYS } from './02-sky-high.js';
import { HEROES_VARIANTS, HEROES_EXPECTED_REPLAYS } from './03-heroes.js';
import { CRADLES_VARIANTS, CRADLES_EXPECTED_REPLAYS } from './04-cradles.js';
import { MY_HEART_VARIANTS, MY_HEART_EXPECTED_REPLAYS } from './05-my-heart.js';
import { INVINCIBLE_VARIANTS, INVINCIBLE_EXPECTED_REPLAYS } from './06-invincible.js';
import { HOPE_VARIANTS, HOPE_EXPECTED_REPLAYS } from './07-hope.js';
import { ADVENTURE_VARIANTS, ADVENTURE_EXPECTED_REPLAYS } from './08-adventure.js';
import { BLANK_VARIANTS, BLANK_EXPECTED_REPLAYS } from './09-blank.js';
import { DIAMOND_VARIANTS, DIAMOND_EXPECTED_REPLAYS } from './10-diamond.js';
import { HOLLAH_VARIANTS, HOLLAH_EXPECTED_REPLAYS } from './11-hollah.js';
import { NEKOZILLA_VARIANTS, NEKOZILLA_EXPECTED_REPLAYS } from './12-nekozilla.js';

/**
 * One playable map: a single (track, variant) pair. Each builtin track
 * produces three of these (normal/hard/extreme); user-created maps from
 * the builder are always `variant: 'normal'`. Today every builtin is a
 * Pulse-mode chart — Circles builtins live in the parallel
 * {@link './circles/index.js' | BUILTIN_CIRCLES_MAPS} list and use the
 * corresponding {@link CirclesBuiltinMap} type so each registry stays
 * narrowly typed for its mode.
 */
export interface BuiltinMap {
  chart: PulseChart;
  expectedReplay: ReplayInputs;
}

/**
 * One song bundled with all of its variants. The home page renders one
 * row per song and lets the player pick a variant; URL routing always
 * resolves to a specific (slug, variant) pair via {@link buildVariantSlug}.
 */
export interface BuiltinPulseTrack {
  /**
   * Bare slug shared by every variant of this track (e.g. `'invincible'`).
   * Variant slugs are derived via {@link buildVariantSlug} when needed.
   */
  baseSlug: string;
  variants: Record<Variant, BuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  baseSlug: string;
  charts: Record<Variant, PulseChart>;
  replays: Record<Variant, ReplayInputs>;
}> = [
  // Tier 1 - first-timer territory.
  { baseSlug: 'on-and-on', charts: ON_AND_ON_VARIANTS, replays: ON_AND_ON_EXPECTED_REPLAYS },
  { baseSlug: 'hope', charts: HOPE_VARIANTS, replays: HOPE_EXPECTED_REPLAYS },
  // Tier 2 - settling in.
  { baseSlug: 'adventure', charts: ADVENTURE_VARIANTS, replays: ADVENTURE_EXPECTED_REPLAYS },
  { baseSlug: 'sky-high', charts: SKY_HIGH_VARIANTS, replays: SKY_HIGH_EXPECTED_REPLAYS },
  // Tier 3 - the heart of the catalogue. Ordered sparse → dense.
  { baseSlug: 'cradles', charts: CRADLES_VARIANTS, replays: CRADLES_EXPECTED_REPLAYS },
  { baseSlug: 'diamond', charts: DIAMOND_VARIANTS, replays: DIAMOND_EXPECTED_REPLAYS },
  { baseSlug: 'blank', charts: BLANK_VARIANTS, replays: BLANK_EXPECTED_REPLAYS },
  { baseSlug: 'heroes', charts: HEROES_VARIANTS, replays: HEROES_EXPECTED_REPLAYS },
  // Tier 4 - leaning hard.
  { baseSlug: 'hollah', charts: HOLLAH_VARIANTS, replays: HOLLAH_EXPECTED_REPLAYS },
  { baseSlug: 'my-heart', charts: MY_HEART_VARIANTS, replays: MY_HEART_EXPECTED_REPLAYS },
  // Tier 5 - veterans only.
  { baseSlug: 'nekozilla', charts: NEKOZILLA_VARIANTS, replays: NEKOZILLA_EXPECTED_REPLAYS },
  { baseSlug: 'invincible', charts: INVINCIBLE_VARIANTS, replays: INVINCIBLE_EXPECTED_REPLAYS },
];

/**
 * Per-track bundles. The home page iterates this for one row per song and
 * picks the requested variant per row. Ordered by song tier then density,
 * matching the original `BUILTIN_PULSE_MAPS` ordering before the variant split.
 */
export const BUILTIN_TRACKS: BuiltinPulseTrack[] = VARIANT_BUNDLES.map((bundle) => ({
  baseSlug: bundle.baseSlug,
  variants: Object.fromEntries(
    VARIANTS.map((v) => [
      v,
      {
        chart: bundle.charts[v],
        expectedReplay: bundle.replays[v],
      } satisfies BuiltinMap,
    ]),
  ) as Record<Variant, BuiltinMap>,
}));

/**
 * Flat list of all (track, variant) maps. The seeder upserts every entry
 * here — each variant is its own row in the `maps` table keyed on its
 * variant slug (e.g. `invincible`, `invincible-hard`, `invincible-extreme`),
 * so leaderboards and progression naturally fork along the variant axis
 * with zero schema changes.
 */
export const BUILTIN_PULSE_MAPS: BuiltinMap[] = BUILTIN_TRACKS.flatMap((track) =>
  VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a single (track, variant) map by either form of slug:
 * - `'invincible'` → normal variant of invincible
 * - `'invincible-hard'` → hard variant of invincible
 * - `'invincible-extreme'` → extreme variant of invincible
 *
 * Returns `undefined` if no track matches.
 *
 * We try a literal `baseSlug` match first so a future track whose bare slug
 * happens to end in `-hard` / `-extreme` (e.g. a song literally called
 * "Going Hard") still resolves correctly — `parseVariantSlug` would
 * otherwise eagerly strip the suffix and look up the wrong track.
 */
export function getBuiltinPulseMapBySlug(slug: string): BuiltinMap | undefined {
  const literalTrack = BUILTIN_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseVariantSlug(slug);
  const track = BUILTIN_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

/** Tracks in display order, one row per song (variants nested inside). */
export function listBuiltinPulseTracks(): BuiltinPulseTrack[] {
  return BUILTIN_TRACKS;
}

/**
 * Flat list, one entry per (track, variant) pair. Used by the seeder and
 * any caller that wants to enumerate every playable map (e.g. the render
 * harness). For UI lists prefer {@link listBuiltinPulseTracks} — it groups
 * variants under their song so the home page doesn't show 36 rows.
 */
export function listBuiltinMaps(): BuiltinMap[] {
  return BUILTIN_PULSE_MAPS;
}
